/**
 * Micro-sonidos del Back Office generados con Web Audio API (0 KB de archivos).
 * Preferencias solo en el navegador (localStorage); nunca en base de datos.
 */
export type AudioProfile = "mechanical" | "wood" | "success" | "alert";
export type AudioVolume = "soft" | "medium" | "normal";

let KEY = "rl-admin-audio";
/** Cambia dónde se guardan las preferencias (p. ej. una por usuario del panel). */
export function setAudioScope(key: string) { KEY = key; }
const VOL: Record<AudioVolume, number> = { soft: 0.05, medium: 0.1, normal: 0.18 };

interface Prefs { enabled: boolean; profile: AudioProfile; volume: AudioVolume }
const DEFAULTS: Prefs = { enabled: false, profile: "wood", volume: "soft" };

export function getAudioPrefs(): Prefs {
  if (typeof window === "undefined") return DEFAULTS;
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return DEFAULTS; }
}
export function setAudioPrefs(p: Partial<Prefs>): Prefs {
  const next = { ...getAudioPrefs(), ...p };
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
  return next;
}

let ctx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const C = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  if (!ctx) ctx = new C();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function tone(c: AudioContext, freq: number, start: number, dur: number, gain: number, type: OscillatorType, cutoff: number) {
  const o = c.createOscillator();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  f.type = "lowpass";
  f.frequency.value = cutoff;
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(f).connect(g).connect(c.destination);
  o.start(start);
  o.stop(start + dur + 0.02);
}

function noiseTick(c: AudioContext, start: number, dur: number, gain: number, cutoff: number) {
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
  const s = c.createBufferSource();
  const f = c.createBiquadFilter();
  const g = c.createGain();
  s.buffer = buf;
  f.type = "bandpass";
  f.frequency.value = cutoff;
  g.gain.value = gain;
  s.connect(f).connect(g).connect(c.destination);
  s.start(start);
}

/** Reproduce un perfil. `force` permite escucharlo aunque el sonido esté apagado. */
export function playUiSound(profile?: AudioProfile, force = false) {
  const prefs = getAudioPrefs();
  if (!prefs.enabled && !force) return;
  const c = getCtx();
  if (!c) return;
  const v = VOL[prefs.volume];
  const t = c.currentTime;
  switch (profile ?? prefs.profile) {
    case "mechanical":
      noiseTick(c, t, 0.012, v * 4, 3200);
      break;
    case "wood":
      tone(c, 420, t, 0.025, v, "sine", 1200);
      noiseTick(c, t, 0.01, v * 1.5, 900);
      break;
    case "success":
      tone(c, 660, t, 0.08, v * 0.8, "sine", 2400);
      tone(c, 990, t + 0.07, 0.1, v * 0.7, "sine", 2400);
      break;
    case "alert":
      tone(c, 220, t, 0.06, v, "triangle", 800);
      break;
  }
}
